/**
 * Client-side rendering entry point for React Router 7
 *
 * Hydration Strategy:
 * - Uses suppressHydrationWarning on body element (in root.tsx)
 * - ClientOnly pattern intentionally creates server/client differences
 * - React 18 handles this gracefully via onRecoverableError
 *
 * Expected hydration mismatches:
 * - LoadingFallback (server) -> Full App (client) is intentional
 * - These are NOT errors - they are the designed behavior
 */

import { HydratedRouter } from 'react-router/dom';
import { startTransition } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { initSentryClient, captureException } from './utils/sentry.client';
import { logger } from './utils/client-logger';

// Initialize Sentry BEFORE React hydration
initSentryClient();

startTransition(() => {
  hydrateRoot(
    document,
    <HydratedRouter />,
    {
      onRecoverableError(error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isHydrationError =
          errorMessage.includes('Hydration') ||
          errorMessage.includes('hydrat') ||
          errorMessage.includes('Text content does not match') ||
          errorMessage.includes('did not match') ||
          errorMessage.includes('#418') ||
          errorMessage.includes('#423') ||
          errorMessage.includes('#425');

        if (isHydrationError) {
          // Log hydration errors for debugging - these should be minimal
          // The ClientOnly pattern in root.tsx intentionally causes ONE expected mismatch
          console.warn('[Hydration Debug]', errorMessage);
        } else {
          logger.error('Client', 'Recoverable error', error);
          // Report non-hydration errors to Sentry
          if (error instanceof Error) {
            captureException(error, { type: 'recoverable-error' });
          }
        }
      },
    }
  );
});
